import React, { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import TableWrapper from "@/utils/TableWrapper";
import type { PropertiesProductRow } from "./AddProductPropertiesView";

/** The row AddProductPropertiesView hands this table, plus the fields the columns and the sort read. */
type ProductTableRow = PropertiesProductRow & {
  country_code?: string;
  updatedAt?: string;
  /** Misspelt timestamp the sort falls back to; kept as the JavaScript read it. */
  uodatedat?: string;
  /** Not a product field: the "Action" column is an accessor column that only renders a button. */
  action?: undefined;
};

export type ProductTableProps = {
  data: ProductTableRow[];
  onAddClick: (event: unknown, row: ProductTableRow) => void;
};

const columnHelper = createColumnHelper<ProductTableRow>();

// SCRUM-2115: search, paging and sorting come from TableWrapper like every
// other admin list. The search box now matches name, country code and the ISO
// timestamp, not only the name; the image URL and the button are excluded.
const ProductTable = ({ data, onAddClick }: ProductTableProps) => {
  // Sort by updatedAt DESC once per data change; a header click re-sorts.
  const sortedData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return [...data].sort((a, b) => {
      const ua = a.updatedAt || a.uodatedat;
      const ub = b.updatedAt || b.uodatedat;
      const da = ua ? new Date(ua).getTime() : 0;
      const db = ub ? new Date(ub).getTime() : 0;
      return db - da; // DESC
    });
  }, [data]);

  const columns = [
    columnHelper.accessor((row) => row.productImg, {
      id: "image",
      header: "",
      enableSorting: false,
      enableGlobalFilter: false,
      cell: (info) => (
        <img
          src={info.getValue()}
          alt="product"
          className="avatar rounded-circle mr-3"
          width={50}
        />
      ),
    }),
    columnHelper.accessor("title", { header: "Name" }),
    columnHelper.accessor("country_code", { header: "Country Code" }),
    columnHelper.accessor((row) => row.updatedAt, {
      id: "updatedAt",
      header: "Last Updated",
      cell: (info) => {
        const v = info.getValue();
        return v ? new Date(v).toLocaleString() : "—";
      },
    }),
    columnHelper.accessor("action", {
      header: "Action",
      enableSorting: false,
      enableGlobalFilter: false,
      cell: ({ row }) => (
        <button
          className="btn btn-sm btn-primary"
          onClick={() => onAddClick(null, row.original)}
        >
          Add Properties
        </button>
      ),
    }),
  ];

  return (
    <TableWrapper
      data={sortedData}
      columns={columns}
      searchLabel="Search by name"
      searchId="productTableSearch"
    />
  );
};

export default ProductTable;
